import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import LYNKED_LOGO_BLACK_SVG from '../../assets/svg/tabi-life-black.svg';
import { Box, Typography } from '@mui/material';
import { useForm } from 'react-hook-form';
import LanguageDropdown from './components/LanguageDropdown';
import { useNavigate } from 'react-router-dom';
import Input from '../../components/Input/Input';
import { buttonStyle, useStyles } from './styles';
import Button from '../../components/Button/Button';
import Auth from '../../libs/auth-wrapper';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import Toast from '../../components/Toast/Toast';
import { useAuthContext } from '../../context/AuthContext/AuthContext';

interface FormValues {
  email: string;
  password: string;
}

function Login() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const classes = useStyles();
  const [openToast, setOpenToast] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const schema = yup.object().shape({
    email: yup.string().required('LOGIN_REQUIRED_EMAIL').email('LOGIN_VALID_EMAIL'),
    password: yup.string().required('LOGIN_REQUIRED_PASSWORD'),
  });
  const { updateUser } = useAuthContext();

  const {
    control,
    formState: { errors },
    handleSubmit,
  } = useForm<FormValues>({
    defaultValues: {
      email: '',
      password: '',
    },
    resolver: yupResolver(schema),
  });

  const onSubmit = handleSubmit(async (data: FormValues) => {
    if (isSubmitting) return;

    let email = data.email;
    let password = data.password;

    try {
      setIsSubmitting(true);
      const res = await Auth.signInEmailPassword(email, password);
      if (!res || !res.user) {
        toast.current = {
          content: t('LOGIN_FAIL_MESSAGE'),
          type: 'error',
        };
        setOpenToast(true);
        setIsSubmitting(false);
        return;
      }

      updateUser(res.user);

      if (res.idToken) {
        localStorage.setItem('idToken', res.idToken);
      }

      navigate('/dashboard', { replace: true });
    } catch (ex: any) {
      toast.current = {
        content: t('LOGIN_FAIL_MESSAGE'),
        type: 'error',
      };
      setOpenToast(true);
      setIsSubmitting(false);
    }
  });

  const toast = useRef<{ type: 'error' | 'success'; content: string }>({
    type: 'success',
    content: '',
  });

  return (
    <>
      <Box className={classes.loginRoot}>
        <Box className={classes.languageDropdown}>
          <LanguageDropdown paperClass={classes.listItemDropdown} />
        </Box>
        <Box className={classes.loginInput}>
          <Box>
            <Box className={classes.logoWrapper}>
              <Box
                id="login-page-lynked-logo"
                className={classes.lynkedLogo}
                style={{
                  backgroundImage: `url(${LYNKED_LOGO_BLACK_SVG})`,
                  backgroundSize: 'contain',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                }}
              ></Box>
            </Box>

            <Box className={classes.textMessages}>
              <span style={{ color: '#AD7E35' }}>MENY </span>
              <span style={{ color: 'black' }}>PORTAL</span>
            </Box>
            <Box>
              <Box>
                <form className={classes.login_form} name="login-form" noValidate autoComplete="off" onSubmit={onSubmit}>
                  <Box className={classes.loginInputText}>
                    <Input
                      control={control}
                      error={(errors?.email?.message && t(errors.email?.message as string)) || ''}
                      name="email"
                      placeholder={t('LOGIN_EMAIL_ADDRESS')}
                      type="text"
                    />
                  </Box>
                  <Box className={classes.loginInputPasswordText}>
                    <Input
                      control={control}
                      error={(errors?.password?.message && t(errors.password?.message as string)) || ''}
                      name="password"
                      placeholder={t('LOGIN_PASSWORD')}
                      type="password"
                    />
                  </Box>
                  <Box>
                    <Button
                      id="login_button"
                      fullWidth={true}
                      title={t('LOGIN_TITLE')}
                      disable={isSubmitting}
                      sxTextStyle={{ fontSize: '14px', fontWeight: '600', lineHeight: '20px', color: '#ffffff' }}
                      buttonStyle={buttonStyle}
                    />
                  </Box>
                </form>
              </Box>
            </Box>
            <Typography className={classes.footerText}>{t('LOGIN_HELPER_HINT')}</Typography>
          </Box>
        </Box>
      </Box>
      <Toast
        content={toast.current?.content}
        variant={toast.current?.type}
        open={openToast}
        onClose={() => {
          setOpenToast(false);
        }}
      />
    </>
  );
}

export default Login;
